import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CurrencyCircleDollar, Receipt, Trophy, CheckCircle, Clock, FloppyDisk } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITION_LABELS = {
  winner: "Champion",
  runner_up: "Runner-up",
  semi_final: "Semi-finalist",
  quarter_final: "Quarter-finalist",
};

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "other", label: "Other" },
];

function formatMoney(amount, symbol) {
  const n = Number(amount || 0);
  return `${symbol} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminFinanceTab({ tournaments }) {
  const [currency, setCurrency] = useState({ currency: "TZS", currency_symbol: "TSh" });
  const [selectedTid, setSelectedTid] = useState("");
  const [config, setConfig] = useState({
    entry_fee: 0,
    prize_mode: "auto_percent",
    prize_pool_total: 0,
    prize_splits: { winner: 50, runner_up: 25, semi_final: 10, quarter_final: 2.5 },
    prize_manual: { winner: 0, runner_up: 0, semi_final: 0, quarter_final: 0 },
  });
  const [fees, setFees] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null); // { type: 'fee'|'payout', item: {...} }

  const symbol = currency.currency_symbol || currency.currency;

  // Load currency settings once
  useEffect(() => {
    axios.get(`${API}/settings/finance`)
      .then(res => setCurrency(res.data))
      .catch(() => {});
  }, []);

  const loadTournament = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const [cfg, feesRes, payoutsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tid}/financial`),
        axios.get(`${API}/tournaments/${tid}/fees`, { withCredentials: true }),
        axios.get(`${API}/tournaments/${tid}/payouts`, { withCredentials: true }),
        axios.get(`${API}/tournaments/${tid}/finance-summary`, { withCredentials: true }),
      ]);
      setConfig({
        entry_fee: cfg.data.entry_fee || 0,
        prize_mode: cfg.data.prize_mode || "auto_percent",
        prize_pool_total: cfg.data.prize_pool_total || 0,
        prize_splits: cfg.data.prize_splits || { winner: 50, runner_up: 25, semi_final: 10, quarter_final: 2.5 },
        prize_manual: cfg.data.prize_manual || { winner: 0, runner_up: 0, semi_final: 0, quarter_final: 0 },
      });
      setFees(feesRes.data);
      setPayouts(payoutsRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tournament finance");
    }
  }, []);

  useEffect(() => { loadTournament(selectedTid); }, [selectedTid, loadTournament]);

  const saveCurrency = async () => {
    try {
      await axios.put(`${API}/settings/finance`, currency, { withCredentials: true });
      toast.success("Currency saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save currency");
    }
  };

  const saveConfig = async () => {
    try {
      const payload = { ...config };
      if (config.prize_mode === "auto_percent") {
        delete payload.prize_manual;
      } else {
        delete payload.prize_splits;
        delete payload.prize_pool_total;
      }
      await axios.put(`${API}/tournaments/${selectedTid}/financial`, payload, { withCredentials: true });
      toast.success("Financial config saved");
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const generateFees = async () => {
    try {
      const res = await axios.post(`${API}/tournaments/${selectedTid}/fees/generate`, {}, { withCredentials: true });
      toast.success(`Generated ${res.data.created} fee entries (${res.data.skipped || 0} skipped)`);
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate fees");
    }
  };

  const generatePayouts = async () => {
    try {
      const res = await axios.post(`${API}/tournaments/${selectedTid}/payouts/generate`, {}, { withCredentials: true });
      toast.success(`Generated ${res.data.created} payout entries (${res.data.skipped || 0} skipped)`);
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate payouts");
    }
  };

  const toggleFeeStatus = async (fee, newStatus) => {
    try {
      await axios.put(`${API}/fees/${fee.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Marked ${newStatus}`);
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const togglePayoutStatus = async (payout, newStatus) => {
    try {
      await axios.put(`${API}/payouts/${payout.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Marked ${newStatus}`);
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const saveEditingEntry = async (updates) => {
    const { type, item } = editingEntry;
    try {
      const url = type === "fee" ? `${API}/fees/${item.id}` : `${API}/payouts/${item.id}`;
      await axios.put(url, updates, { withCredentials: true });
      toast.success("Updated");
      setEditingEntry(null);
      loadTournament(selectedTid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-finance-tab">
      {/* Currency Setting */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2">
          <CurrencyCircleDollar className="w-5 h-5 text-primary" />Currency Settings
        </CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Currency Code</Label>
            <Input value={currency.currency} onChange={e => setCurrency({ ...currency, currency: e.target.value.toUpperCase() })}
              placeholder="TZS" className="w-32" data-testid="finance-currency-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Symbol</Label>
            <Input value={currency.currency_symbol} onChange={e => setCurrency({ ...currency, currency_symbol: e.target.value })}
              placeholder="TSh" className="w-24" data-testid="finance-currency-symbol-input" />
          </div>
          <Button size="sm" onClick={saveCurrency} data-testid="finance-save-currency-btn">
            <FloppyDisk className="w-4 h-4 mr-1" />Save
          </Button>
        </CardContent>
      </Card>

      {/* Tournament Picker */}
      <Card>
        <CardHeader><CardTitle className="text-base">Tournament Financials</CardTitle></CardHeader>
        <CardContent>
          <Label className="text-xs text-muted-foreground">Select tournament</Label>
          <Select value={selectedTid} onValueChange={setSelectedTid}>
            <SelectTrigger className="w-full md:w-96" data-testid="finance-tournament-select">
              <SelectValue placeholder="Choose a tournament..." />
            </SelectTrigger>
            <SelectContent>
              {tournaments.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name} · {t.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedTid && (
        <>
          {/* Summary */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fees Collected</p>
                <p className="text-xl font-bold text-primary" data-testid="finance-summary-fees-collected">
                  {formatMoney(summary.fees.collected, symbol)}
                </p>
                <p className="text-xs text-muted-foreground">of {formatMoney(summary.fees.total_expected, symbol)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fees Outstanding</p>
                <p className="text-xl font-bold text-destructive" data-testid="finance-summary-fees-outstanding">
                  {formatMoney(summary.fees.outstanding, symbol)}
                </p>
                <p className="text-xs text-muted-foreground">{summary.fees.unpaid_count} unpaid</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Prizes Paid</p>
                <p className="text-xl font-bold" data-testid="finance-summary-prizes-paid">
                  {formatMoney(summary.payouts.paid, symbol)}
                </p>
                <p className="text-xs text-muted-foreground">of {formatMoney(summary.payouts.total, symbol)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Net Balance</p>
                <p className={`text-xl font-bold ${summary.net_balance >= 0 ? "text-primary" : "text-destructive"}`}
                   data-testid="finance-summary-net-balance">
                  {formatMoney(summary.net_balance, symbol)}
                </p>
                <p className="text-xs text-muted-foreground">fees − prizes paid</p>
              </CardContent></Card>
            </div>
          )}

          {/* Config */}
          <Card>
            <CardHeader><CardTitle className="text-base">Entry Fee & Prize Pool</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Entry fee per player ({symbol})</Label>
                  <Input type="number" min="0" step="0.01" value={config.entry_fee}
                    onChange={e => setConfig({ ...config, entry_fee: parseFloat(e.target.value) || 0 })}
                    className="w-40" data-testid="finance-entry-fee-input" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Prize mode</Label>
                  <Select value={config.prize_mode} onValueChange={v => setConfig({ ...config, prize_mode: v })}>
                    <SelectTrigger className="w-56" data-testid="finance-prize-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto_percent">Auto: split a total pool by %</SelectItem>
                      <SelectItem value="manual_cash">Manual: set cash per placement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {config.prize_mode === "auto_percent" ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Total prize pool ({symbol})</Label>
                    <Input type="number" min="0" step="0.01" value={config.prize_pool_total}
                      onChange={e => setConfig({ ...config, prize_pool_total: parseFloat(e.target.value) || 0 })}
                      className="w-40" data-testid="finance-prize-pool-total-input" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {["winner", "runner_up", "semi_final", "quarter_final"].map(k => (
                      <div key={k}>
                        <Label className="text-xs text-muted-foreground">{POSITION_LABELS[k]} (%)</Label>
                        <Input type="number" min="0" max="100" step="0.5" value={config.prize_splits[k]}
                          onChange={e => setConfig({
                            ...config,
                            prize_splits: { ...config.prize_splits, [k]: parseFloat(e.target.value) || 0 },
                          })}
                          data-testid={`finance-split-${k}-input`} />
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          ≈ {formatMoney((config.prize_pool_total * (config.prize_splits[k] || 0) / 100), symbol)}
                          {k === "semi_final" ? " each" : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["winner", "runner_up", "semi_final", "quarter_final"].map(k => (
                    <div key={k}>
                      <Label className="text-xs text-muted-foreground">{POSITION_LABELS[k]} ({symbol})</Label>
                      <Input type="number" min="0" step="0.01" value={config.prize_manual[k]}
                        onChange={e => setConfig({
                          ...config,
                          prize_manual: { ...config.prize_manual, [k]: parseFloat(e.target.value) || 0 },
                        })}
                        data-testid={`finance-manual-${k}-input`} />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={saveConfig} data-testid="finance-save-config-btn">
                  <FloppyDisk className="w-4 h-4 mr-1" />Save Config
                </Button>
                <Button size="sm" variant="outline" onClick={generateFees} data-testid="finance-generate-fees-btn">
                  <Receipt className="w-4 h-4 mr-1" />Generate Fee Entries
                </Button>
                <Button size="sm" variant="outline" onClick={generatePayouts} data-testid="finance-generate-payouts-btn">
                  <Trophy className="w-4 h-4 mr-1" />Generate Payouts (from placements)
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Fee Entries */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />Entry Fees ({fees.length})
            </CardTitle></CardHeader>
            <CardContent className="p-0">
              {fees.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">No fee entries. Click "Generate Fee Entries" above.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Paid On</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {fees.map(f => (
                      <TableRow key={f.id} data-testid={`fee-row-${f.id}`}>
                        <TableCell className="font-medium">{f.player_name}</TableCell>
                        <TableCell>{formatMoney(f.amount, symbol)}</TableCell>
                        <TableCell>
                          <Badge className={
                            f.status === "paid" ? "bg-primary/20 text-primary border-primary/30" :
                            f.status === "waived" ? "bg-muted text-muted-foreground border-border" :
                            "bg-destructive/20 text-destructive border-destructive/30"
                          }>{f.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{f.payment_method || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{f.paid_on ? new Date(f.paid_on).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right space-x-1">
                          {f.status !== "paid" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => toggleFeeStatus(f, "paid")}
                              data-testid={`fee-mark-paid-${f.id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" />Paid
                            </Button>
                          )}
                          {f.status === "paid" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => toggleFeeStatus(f, "unpaid")}
                              data-testid={`fee-mark-unpaid-${f.id}`}>
                              Undo
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => setEditingEntry({ type: "fee", item: f })}
                            data-testid={`fee-edit-${f.id}`}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Payouts */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-secondary" />Prize Payouts ({payouts.length})
            </CardTitle></CardHeader>
            <CardContent className="p-0">
              {payouts.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">No payouts. Generate after placements are recorded.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Position</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Paid On</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {payouts.map(p => (
                      <TableRow key={p.id} data-testid={`payout-row-${p.id}`}>
                        <TableCell className="capitalize font-medium">{POSITION_LABELS[p.position] || p.position}</TableCell>
                        <TableCell>{p.player_name}</TableCell>
                        <TableCell className="text-primary font-semibold">{formatMoney(p.amount, symbol)}</TableCell>
                        <TableCell>
                          <Badge className={
                            p.status === "paid" ? "bg-primary/20 text-primary border-primary/30" :
                            "bg-secondary/20 text-secondary border-secondary/30"
                          }>{p.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.payment_method || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.paid_on ? new Date(p.paid_on).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right space-x-1">
                          {p.status !== "paid" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => togglePayoutStatus(p, "paid")}
                              data-testid={`payout-mark-paid-${p.id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" />Paid
                            </Button>
                          )}
                          {p.status === "paid" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => togglePayoutStatus(p, "pending")}
                              data-testid={`payout-mark-pending-${p.id}`}>
                              Undo
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => setEditingEntry({ type: "payout", item: p })}
                            data-testid={`payout-edit-${p.id}`}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Details Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={open => !open && setEditingEntry(null)}>
        <DialogContent data-testid="finance-entry-dialog">
          <DialogHeader><DialogTitle>
            Edit {editingEntry?.type === "fee" ? "Fee Entry" : "Payout"} — {editingEntry?.item?.player_name}
          </DialogTitle></DialogHeader>
          {editingEntry && <EntryEditForm
            key={editingEntry.item.id}
            entry={editingEntry.item}
            type={editingEntry.type}
            onSave={saveEditingEntry}
            onCancel={() => setEditingEntry(null)}
          />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntryEditForm({ entry, type, onSave, onCancel }) {
  const [form, setForm] = useState({
    status: entry.status || (type === "fee" ? "unpaid" : "pending"),
    amount: entry.amount ?? 0,
    payment_method: entry.payment_method || "",
    reference_no: entry.reference_no || "",
    paid_on: entry.paid_on ? entry.paid_on.slice(0, 10) : "",
    notes: entry.notes || "",
  });

  const statusOptions = type === "fee"
    ? [{ value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }, { value: "waived", label: "Waived" }]
    : [{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="entry-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Amount</Label>
          <Input type="number" step="0.01" value={form.amount}
            onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
            data-testid="entry-amount-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Payment method</Label>
          <Select value={form.payment_method || "_none"}
            onValueChange={v => setForm({ ...form, payment_method: v === "_none" ? "" : v })}>
            <SelectTrigger data-testid="entry-method-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Reference #</Label>
          <Input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })}
            data-testid="entry-reference-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Paid on</Label>
        <Input type="date" value={form.paid_on} onChange={e => setForm({ ...form, paid_on: e.target.value })}
          data-testid="entry-date-input" />
      </div>
      <div>
        <Label className="text-xs">Notes</Label>
        <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          data-testid="entry-notes-input" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(form)} data-testid="entry-save-btn">
          <FloppyDisk className="w-4 h-4 mr-1" />Save
        </Button>
      </div>
    </div>
  );
}
